import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { IPickRoundSpot } from "neo-games/Engine/Communication/Game/Types/IPickRoundSpot";
import { GameManager } from "../Managers/GameManager";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { IPickRound } from "neo-games/Engine/Communication/Game/Types/IPickRound";
import { Tweens } from "../Components/Effects/Tweens";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { PlayList } from "../Components/Sounds/PlayList";

var PickRoundSpotStatus = Communication.Game.PickRoundSpotStatus;
var PickRoundSpotValueType = Communication.Game.PickRoundSpotValueType;

/**
 * WinAnimatorPick uses paylines and symbols to create presentation of wins to user.
 * Also handle high wins animation for presentation special screens to user when win is bigger than some defined values.
 * @class WinAnimatorPick
 * @extends ContainerElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorPick extends WinAnimator {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    //******************************************************************************************************************
    //  TIMER STATUSES
    //******************************************************************************************************************

    protected static readonly Start: string = "start";
    protected static readonly SelectUpdate: string = "selectUpdate";
    protected static readonly Select: string = "select";
    protected static readonly UpdateCounters: string = "updateCounters";
    protected static readonly ShowWin: string = "showWin";
    protected static readonly ShownEnabled: string = "shownEnabled";
    protected static readonly EmitFinishWinAnimator: string = "emitFinishWinAnimator";
    protected static readonly EmitFinishWinCalculation: string = "emitFinishCalculation";
    public static readonly FinishWinCalculation: string = "eventFinishWinCalculation";

    protected static readonly Idle: string = "idle";

    protected static readonly CycleWins: string = "cycleWins";

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected elementInstance: Array<Object> = [];

    protected winStepDuration: number = 10;

    private spots: Array<SwitchElement>;
    private spotsContainer: ContainerElement;
    private spotsWinShape: SwitchElement;
    private spotsWinValues: SwitchElement;
    private spotsWinValuesCounters: Array<ImageTextCounterElement>;
    private spotsNoWinValuesCounters: Array<ImageTextCounterElement>;
    private _multipliers: Array<SwitchElement>;
    private multipliersContainer: ContainerElement;
    private multipliersWinShape: SwitchElement;
    private _multipliersWinValues: SwitchElement;
    private bonusTable: SwitchElement;
    private _multipliersWinValuesCounters: Array<ImageTextCounterElement>;
    private _multipliersNoWinValuesCounters: Array<ImageTextCounterElement>;
    private _pickSelected: number = 0;
    private _actualReel: number = -1;
    protected currentSpots: IPickRoundSpot[];
    protected currentRound: IPickRound;
    private isInit: boolean = false;
    private _finishWinCalculationDelay: FrameTimerEventEmitter;
    private shownEnabled: boolean = false;
    protected static readonly IdleJack: string = "idleJack";
    private _idleJackStart: FrameTimerEventEmitter;
    private _pickSpotNumberCounting: FrameTimerEventEmitter;
    private _pickMultiplierNumberCounting: FrameTimerEventEmitter;
    private stepIdleJack: string = "";
    private multiplierCounter: number = 0;
    private _playMultiplierSounds: FrameTimerEventEmitter;

    private pickInfoWinNumber: ImageTextCounterElement;
    private pickInfoMultiplierNumber: ImageTextCounterElement;

    protected revealedSpotId: number;
    protected revealedSpotValue: number;
    protected revealedMultiplierId: number;
    protected revealedMultiplierValue: number;

    // JACK
    protected idleJackMessageId: number; // idle animation id for message waiting
    protected idleJackMessages: number = 4; // number of idle animation id for message waiting

    constructor() {
        super();
        this.shownEnabled = false;

        this._finishWinCalculationDelay = CreateFrameTimer("finishWinCalculationDelay");
        this._finishWinCalculationDelay.on(FrameTimerEvents.Complete, this.finishWinCalculation.bind(this));

        this._idleJackStart = CreateFrameTimer("idleJackStart");
        this._idleJackStart.on(FrameTimerEvents.Complete, this.idleJack.bind(this));

        this._pickSpotNumberCounting = CreateFrameTimer("pickSpotNumberCounting");
        this._pickSpotNumberCounting.on(FrameTimerEvents.Complete, this.pickSpotNumberCounting.bind(this));

        this._pickMultiplierNumberCounting = CreateFrameTimer("pickMultiplierNumberCounting");
        this._pickMultiplierNumberCounting.on(FrameTimerEvents.Complete, this.pickMultiplierNumberCounting.bind(this));

        this._playMultiplierSounds = CreateFrameTimer("playMultiplierSounds");
        this._playMultiplierSounds.on(FrameTimerEvents.Complete, this.playMultiplierSounds.bind(this));
    }
    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    public createByNode(node: Element): void {
        super.createByNode(node);
    }

    public initializeCustom(
        spots: Array<SwitchElement>,
        spotsWinValues: SwitchElement,
        spotsWinValuesCounters: Array<ImageTextCounterElement>,
        spotsNoWinValuesCounters: Array<ImageTextCounterElement>,
        multipliers: Array<SwitchElement>,
        multipliersWinValues: SwitchElement,
        multipliersWinValuesCounters: Array<ImageTextCounterElement>,
        multipliersNoWinValuesCounters: Array<ImageTextCounterElement>,
        spotsWinShape: SwitchElement,
        spotsContainer: ContainerElement,
        multipliersContainer: ContainerElement,
        multipliersWinShape: SwitchElement,
        pickInfoWinNumber: ImageTextCounterElement,
        pickInfoMultiplierNumber: ImageTextCounterElement,
        bonusTable: SwitchElement,
    ): void {
        this.spots = spots;
        this.spotsWinValues = spotsWinValues;
        this.spotsWinValuesCounters = spotsWinValuesCounters;
        this.spotsNoWinValuesCounters = spotsNoWinValuesCounters;
        this._multipliers = multipliers;
        this._multipliersWinValues = multipliersWinValues;
        this._multipliersWinValuesCounters = multipliersWinValuesCounters;
        this._multipliersNoWinValuesCounters = multipliersNoWinValuesCounters;
        this.idleJackMessageId = Math.floor(Math.random() * this.idleJackMessages);
        this.spotsWinShape = spotsWinShape;
        this.spotsContainer = spotsContainer;
        this.multipliersContainer = multipliersContainer;
        this.multipliersWinShape = multipliersWinShape;
        this.bonusTable = bonusTable;

        this.pickInfoWinNumber = pickInfoWinNumber;
        this.pickInfoMultiplierNumber = pickInfoMultiplierNumber;

        this.createTimer();
    }

    /**
     *
     * @param {Object} element
     */
    public addElementInstance(element: Object): void {
        this.elementInstance[Object.keys(element)[0]] = element[Object.keys(element)[0]];
    }

    /**
     *
     * @param spotsData
     */
    public showSpots(spotsData: IPickRoundSpot[], intro?: boolean): void {
        this._actualReel = -1;
        this.currentSpots = spotsData;
        this.shownEnabled = false;

        if (
            GameManager.pickResponse.round.remainingPicks != null &&
            GameManager.pickResponse.round.remainingPicks == 2
        ) {
            if (intro != null && intro) {
                for (let i = 0; i < this.spots.length; i++) {
                    this.spots[i].goToCase("NORMAL_" + i);
                }
                this.spotsWinValues.goToCase("EMPTY");

                for (let i = 0; i < this._multipliers.length; i++) {
                    this._multipliers[i].goToCase("EMPTY");
                }
                this._multipliersWinValues.goToCase("EMPTY");
            } else {
                for (let i = 0; i < this.spots.length; i++) {
                    this.spots[i].goToCase("NORMAL_" + i);
                }

                for (let i = 0; i < this._multipliers.length; i++) {
                    this._multipliers[i].goToCase("EMPTY");
                }
            }
        } else if (
            GameManager.pickResponse.round.remainingPicks != null &&
            GameManager.pickResponse.round.remainingPicks == 1
        ) {
            for (let i = 0; i < this._multipliers.length; i++) {
                this._multipliers[i].goToCase("NORMAL_" + i);
            }
        } else {
            for (let i = 0; i < this.spots.length; i++) {
                this.spots[i].goToCase("EMPTY");
            }
        }
    }

    public hideSpots(): void {
        this.shownEnabled = false;

        for (let i = 0; i < this.spots.length; i++) {
            this.spots[i].goToCase("EMPTY");
        }

        for (let i = 0; i < this._multipliers.length; i++) {
            this._multipliers[i].goToCase("EMPTY");
        }
    }

    /**3
     *
     * @param spotsData
     */
    public setSpotsData(spotsData: IPickRoundSpot[]): void {
        this.currentSpots = spotsData;
    }

    /**
     *
     * @param spotId
     * @param useValue
     */
    protected winSpotsGoToCase(spotId: number, useValue: boolean, showEnabled: boolean = false): void {
        let id: number;
        let status: Communication.Game.PickRoundSpotStatus;
        let value: number = null;
        let valueType: Communication.Game.PickRoundSpotValueType;
        let addDelay: boolean = true;

        let spotData: IPickRoundSpot = this.currentSpots.filter((currentSpot) => currentSpot.id == spotId)[0];
        if (spotData.id != null) {
            id = spotData.id;
        }
        if (spotData.status != null) {
            status = spotData.status;
        }
        if (spotData.value != null) {
            value = parseInt(spotData.value + "");
        }
        if (spotData.valueType != null) {
            valueType = spotData.valueType;
        }

        if (spotId != null && status != null) {
            if (
                (this.spots != null && this.spots[spotId] != null) ||
                (this._multipliers != null && this._multipliers[spotId - 5] != null)
            ) {
                switch (status) {
                    case PickRoundSpotStatus.Enabled:
                        if (valueType === PickRoundSpotValueType.Multiplier && showEnabled) {
                            // this._multipliers[spotId-5].goToCase('SHOW');
                        }

                        if (valueType === PickRoundSpotValueType.Cash) {
                            this.spots[spotId].goToCase("BONUS_REVEALED");
                        }

                        break;
                    case PickRoundSpotStatus.Disabled:
                        if (valueType === PickRoundSpotValueType.Multiplier) {
                            this._multipliers[spotId - 5].goToCase("EMPTY");
                        }

                        if (valueType === PickRoundSpotValueType.Cash) {
                            this.spots[spotId].goToCase("EMPTY");
                        }
                        break;
                    case PickRoundSpotStatus.Revealed:
                        if (value != null && valueType != null) {
                            if (
                                GameManager.pickResponse.round.remainingPicks != null &&
                                GameManager.pickResponse.round.remainingPicks != 0
                            ) {
                                if (valueType === PickRoundSpotValueType.Cash && !showEnabled) {
                                    if (this.isInit) {
                                        this.spots[spotId].goToCase("EMPTY");
                                        this.spotsWinValues.goToCase("EMPTY");
                                        this.spotsWinValuesCounters[spotId].start(0, spotData.value, 0);
                                        this.pickInfoWinNumber.setBitmapText(spotData.value);
                                        this.pickInfoWinNumber.updatePivots();
                                        this.pickInfoWinNumber.alpha = 1;
                                    } else {
                                        this.spots[spotId].goToCase("BONUS_REVEALED_" + spotId);
                                        this.spotsWinValues.goToCase("PICK_" + spotId);

                                        this.spotsWinShape.goToCase("SHOW");

                                        this.spotsContainer.setChildIndex(this.spotsWinShape, 5);
                                        this.spotsContainer.setChildIndex(this.spots[spotId], 5);

                                        this.revealedSpotId = spotId;
                                        this.revealedSpotValue = spotData.value;

                                        this._pickSpotNumberCounting.start(120);
                                    }
                                }
                            }

                            if (
                                GameManager.pickResponse.round.remainingPicks != null &&
                                GameManager.pickResponse.round.remainingPicks == 0
                            ) {
                                if (valueType === PickRoundSpotValueType.Multiplier && !showEnabled) {
                                    if (this.isInit) {
                                        this._multipliers[spotId - 5].goToCase(
                                            "BONUS_REVEALED_" + (spotId - 5) + "_INIT",
                                        );
                                        this._multipliersWinValues.goToCase("PICK_" + (spotId - 5) + "_CHOSEN");
                                        this._multipliersWinValuesCounters[spotId - 5].start(
                                            spotData.value,
                                            spotData.value,
                                            1,
                                        );
                                    } else {
                                        this._multipliers[spotId - 5].goToCase("BONUS_REVEALED_" + (spotId - 5));
                                        this._multipliersWinValues.goToCase("PICK_" + (spotId - 5));

                                        this.multipliersWinShape.goToCase("SHOW");

                                        this.multipliersContainer.setChildIndex(this.multipliersWinShape, 2);
                                        this.multipliersContainer.setChildIndex(this._multipliers[spotId - 5], 2);

                                        this.revealedMultiplierId = spotId;
                                        this.revealedMultiplierValue = spotData.value;
                                        this._pickMultiplierNumberCounting.start(120);
                                    }
                                }

                                if (
                                    GameManager.pickResponse.round.remainingPicks != null &&
                                    GameManager.pickResponse.round.remainingPicks == 0
                                ) {
                                    if (valueType === PickRoundSpotValueType.Cash && !showEnabled) {
                                        this.spots[spotId].goToCase("EMPTY");
                                        this.spotsWinValues.goToCase("EMPTY");
                                        this.spotsWinValuesCounters[spotId].start(0, spotData.value, 0);
                                    }
                                }
                            }
                        }
                        break;
                    case PickRoundSpotStatus.Shown:
                        if (showEnabled || (this.isInit && GameManager.pickResponse.round.remainingPicks == 0)) {
                            if (this.isInit && GameManager.pickResponse.round.remainingPicks == 0) {
                            } else {
                                this.startTimer(WinAnimatorPick.EmitFinishWinAnimator, 300);
                            }

                            addDelay = false;
                            if (
                                GameManager.pickResponse.round.remainingPicks != null &&
                                GameManager.pickResponse.round.remainingPicks == 0
                            ) {
                                if (valueType === PickRoundSpotValueType.Multiplier) {
                                    if (this.isInit) {
                                        this._multipliers[spotId - 5].goToCase("BONUS_SHOWN_" + (spotId - 5) + "_INIT");
                                    } else {
                                        this._multipliers[spotId - 5].goToCase("BONUS_SHOWN_" + (spotId - 5));
                                    }
                                    this._multipliersWinValuesCounters[spotId - 5].start(
                                        spotData.value,
                                        spotData.value,
                                        1,
                                    );
                                    this._multipliersNoWinValuesCounters[spotId - 5].start(
                                        spotData.value,
                                        spotData.value,
                                        1,
                                    );
                                }
                            } else {
                                if (valueType === PickRoundSpotValueType.Cash) {
                                    if (this.isInit) {
                                        this.spots[spotId].goToCase("BONUS_SHOWN_" + spotId + "_INIT");
                                    } else {
                                        this.spots[spotId].goToCase("BONUS_SHOWN_" + spotId);
                                    }
                                    this.spotsWinValuesCounters[spotId].start(spotData.value, spotData.value, 1);
                                    this.spotsNoWinValuesCounters[spotId].start(spotData.value, spotData.value, 1);
                                }
                            }
                        } else if (this.isInit && GameManager.pickResponse.round.remainingPicks == 1) {
                            if (valueType === PickRoundSpotValueType.Cash) {
                                this.spots[spotId].goToCase("EMPTY");
                            }
                        } else if (!showEnabled) {
                            if (valueType === PickRoundSpotValueType.Cash) {
                                this.spots[spotId].goToCase("TEMP_SHOWN_" + spotId);
                            }

                            if (valueType === PickRoundSpotValueType.Multiplier) {
                                this._multipliers[spotId - 5].goToCase("TEMP_SHOWN_" + (spotId - 5));
                            }
                        }
                        break;
                    default:
                        break;
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no spots or spot with id");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no id or status");
            }
        }
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public pickSpotNumberCounting(): void {
        this.spotsWinValuesCounters[this.revealedSpotId].realign();
        this.spotsWinValuesCounters[this.revealedSpotId].start(0, this.revealedSpotValue, 90);
        this.pickInfoWinNumber.start(this.revealedSpotValue, this.revealedSpotValue, 0);

        PlayList.loop("SND_BonusCreditboxLoop");

        this._finishWinCalculationDelay.start(95);
    }

    setSpotValue(): void {
        this.pickInfoWinNumber.alpha = 1;
        this.pickInfoWinNumber.start(this.revealedSpotValue, this.revealedSpotValue, 0);
    }

    public pickMultiplierNumberCounting(): void {
        let points: Array<PIXI.Point> = [];
        for (let i = 0; i < this.revealedMultiplierValue; i++) {
            points.push(new PIXI.Point(1, 1));
            points.push(new PIXI.Point(1.2, 1.2));
            points.push(new PIXI.Point(1, 1));
        }

        this._multipliersWinValuesCounters[this.revealedMultiplierId - 5].start(
            0,
            this.revealedMultiplierValue,
            10 * this.revealedMultiplierValue,
        );

        Tweens.removeTweens(this._multipliersWinValuesCounters[this.revealedMultiplierId - 5]);
        Tweens.scaleTween(this._multipliersWinValuesCounters[this.revealedMultiplierId - 5], {
            tweenValues: points,
            tweenDuration: 10 * this.revealedMultiplierValue,
            tweenEase: "backInOut",
        });

        // this.pickInfoMultiplierNumber.setBitmapText(this.revealedMultiplierValue);
        this.pickInfoMultiplierNumber.start(this.revealedMultiplierValue, this.revealedMultiplierValue, 0);

        BaseView.Jack.setDynamicTime(10 * this.revealedMultiplierValue);
        BaseView.Jack.goToCase("TENSE_BEFORE_END");

        // PlayList.play('SND_BonusMultiplier1');

        this.multiplierCounter = 0;

        this._playMultiplierSounds.start(5);

        this._finishWinCalculationDelay.start(10 * this.revealedMultiplierValue + 5);
    }

    playMultiplierSounds(): void {
        if (this.multiplierCounter == this.revealedMultiplierValue) {
            return;
        }

        if (this.multiplierCounter < 4) {
            PlayList.play("SND_BonusMultiplier1");
        } else if (this.multiplierCounter < 7) {
            PlayList.play("SND_BonusMultiplier2");
        } else {
            PlayList.play("SND_BonusMultiplier3");
        }

        this.multiplierCounter++;

        this._playMultiplierSounds.start(10);
    }

    /**
     *
     * @param round
     */
    public showPickWins(round: IPickRound): boolean {
        this.currentRound = round;
        this.currentWin = round.totalWin;

        this.shownEnabled = false;

        this.showFirstWins();

        if (this.currentRound.remainingPicks == 1) {
            this.startTimer(WinAnimatorPick.ShownEnabled, 120);
        } else {
            this.startTimer(WinAnimatorPick.ShownEnabled, 120 + this.revealedMultiplierValue * 10);
        }
        return true;
    }

    public showInitWins(
        wins: Array<Communication.Game.IWin>,
        win: number,
        spots?: IPickRoundSpot[],
        remainigPicks?: number,
    ): void {
        if (remainigPicks != null && remainigPicks == 0) {
            // this.shownEnabled = true;
        }
        this.isInit = true;
        this.currentSpots = spots;
        for (let key in this.currentSpots) {
            // Add spot to win case
            this.winSpotsGoToCase(this.currentSpots[key].id, false);
        }
        this.isInit = false;
    }

    protected showFirstWins(showEnabled: boolean = false): void {
        // Add spot to win case
        for (let key in this.currentSpots) {
            // Add spot to win case
            this.winSpotsGoToCase(this.currentSpots[key].id, true, showEnabled);
        }
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin: boolean = false): boolean {
        // Timer
        this.stopTimer();

        if (showLastSymbols && !spin) {
            this.startTimer(WinAnimatorPick.Idle, this.getInitialIdleDelay());
        }

        if (this.currentAnimationIndex != null) {
            if (this.currentAnimationFinished) {
                this.currentWins = null;
                this.currentWin = null;

                return false;
            } else {
                this.currentWins = null;
                this.currentWin = null;

                return this.currentMultiplierIndex >= 3;
            }
        } else {
            this.currentWins = null;
            this.currentWin = null;

            return this.currentMultiplierIndex >= 3;
        }
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorPick.Start:
            case WinAnimatorPick.SelectUpdate:
            case WinAnimatorPick.Select:
            case WinAnimatorPick.UpdateCounters:
            case WinAnimatorPick.ShowWin:
                break;
            case WinAnimatorPick.ShownEnabled:
                this.shownEnabled = true;
                if (
                    GameManager.pickResponse.round.remainingPicks != null &&
                    GameManager.pickResponse.round.remainingPicks == 1
                ) {
                    BaseView.Jack.setDynamicTime(5);
                    BaseView.Jack.goToCase("TENSE_BEFORE_END");
                }
                this.showFirstWins(true);
                break;
            case WinAnimatorPick.Idle:
                this.idle();
                break;
            case WinAnimatorPick.EmitFinishWinAnimator:
                if (
                    GameManager.pickResponse.round.remainingPicks != null &&
                    GameManager.pickResponse.round.remainingPicks == 0
                ) {
                    this.stopTimer();
                }

                this.emit(WinAnimator.FinishWinAnimator);
                break;
            case WinAnimatorPick.EmitFinishWinCalculation:
                break;
        }
    }

    finishWinCalculation(): void {
        if (GameManager.pickResponse.round.remainingPicks == 1) {
            this.emit(WinAnimatorPick.FinishWinCalculation);
            // this.spotsWinValuesCounters[this.revealedSpotId].makeTween(0);
            this.spotsWinValues.goToCase("BOUNCE_" + this.revealedSpotId);
            this.bonusTable.goToCase("BONUS_1_COUNT");
            this.stopTimer();
            BaseView.Jack.goToCase("BONUS_1_COUNT");
        } else if (GameManager.pickResponse.round.remainingPicks == 0) {
            this._multipliersWinValues.goToCase("BOUNCE_" + (this.revealedMultiplierId - 5));
            this._multipliersWinValuesCounters[this.revealedMultiplierId - 5].makeTween(0);
            this.bonusTable.goToCase("BONUS_2_COUNT");
            this.stopTimer();
            BaseView.Jack.goToCase("BONUS_2_COUNT");
            PlayList.play("SND_BonusMultiplierLast");
        }
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {}

    protected idleJack(): void {
        // 3 main and idle animations for messages

        let actualIdleStep: number = Math.floor(Math.random() * this.idleJackMessages);
        while (this.idleJackMessageId == actualIdleStep) {
            actualIdleStep = Math.floor(Math.random() * this.idleJackMessages);
        }

        this.idleJackMessageId = actualIdleStep;
        if (this.stepIdleJack != "") {
            BaseView.Jack.goToCase("IDLE_MULTIPLIER_" + this.idleJackMessageId);
        } else {
            BaseView.Jack.goToCase("IDLE_MESSAGE_" + this.idleJackMessageId);
        }
        this._idleJackStart.start(480);
    }

    public startIdleJack(step: string = ""): void {
        this.stepIdleJack = step;
        this._idleJackStart.start(480);
    }

    public idleSpots(): void {
        for (let i = 0; i < this.spots.length; i++) {
            this.spots[i].goToCase("IDLE_" + i);
        }
    }

    public idleMultipliers(): void {
        for (let i = 0; i < this._multipliers.length; i++) {
            this._multipliers[i].goToCase("IDLE_" + i);
        }
    }

    public stopTimer(): void {
        this._idleJackStart.stop();
    }
}

export const WinAnimatorPickAttributes = {
    /**
     * Sounds property initialize playlist for winAnimator.
     * @property Sounds
     * @type String
     * @default null
     */
    SOUNDS: "Sounds",
    /**
     * Name of config for win animator.
     * @property Configs
     * @type string
     * @default null
     */
    CONFIGS: "Configs",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorPick.ts
 **/